import { atr } from "./indicators";

export const DEFAULT_SETTINGS = {
  capital: 10000,
  allocPct: 100, // entry notional as % of current equity (100 = fully invested, no leverage)
  spreadBps: 1, // full bid/ask spread in basis points of price (1 bp = 0.01%), half paid per side
  commissionBps: 0, // per side
  slippageBps: 0.5, // per side
  entryOrderType: "market", // "market" | "limit" | "stop", valid for the next candle only
  entryOffsetBps: 0,
  longFinancingBpsPerDay: 0, // carrying cost charged for each elapsed 24 hours in a long position
  shortFinancingBpsPerDay: 0, // positive = cost; negative = credit
  slType: "off", // stop loss distance: "off" | "atr" (multiples of ATR14) | "pct" (% of entry price)
  slValue: 2,
  tpType: "off", // take profit: "off" | "atr" | "pct" | "rr" (reward:risk multiple of the stop distance, needs a stop loss)
  tpValue: 4,
  trailing: false, // trail the stop behind the best price since entry (needs a stop loss)
  allowShort: true,
};

/** OHLC entry fill. Limit/stop orders use the prior close as their price anchor and expire
 * after the next candle. A gap through an order fills at the candle open. */
function entryFillPrice(side, signalClose, candle, settings) {
  if (settings.entryOrderType !== "limit" && settings.entryOrderType !== "stop") return candle.open;
  const anchor = Number(signalClose);
  const offset = Math.max(0, Number(settings.entryOffsetBps) || 0) / 1e4;
  if (!Number.isFinite(anchor) || anchor <= 0) return NaN;
  const buy = side === 1;
  const isLimit = settings.entryOrderType === "limit";
  const price = anchor * (buy === isLimit ? 1 - offset : 1 + offset);
  if (isLimit) {
    if (buy && candle.open <= price) return candle.open;
    if (!buy && candle.open >= price) return candle.open;
    if ((buy && candle.low <= price) || (!buy && candle.high >= price)) return price;
    return NaN;
  }
  if (buy && candle.open >= price) return candle.open;
  if (!buy && candle.open <= price) return candle.open;
  if ((buy && candle.high >= price) || (!buy && candle.low <= price)) return price;
  return NaN;
}

/** Backtest the signed number of open trade slots for strategies that support pyramiding.
 *  Each slot receives an equal share of the configured allocation, so using more slots does
 *  not increase total exposure beyond allocPct. */
/**
 * Position size is the notional opened as a share of current equity. Units are calculated
 * once at entry and held fixed until exit; each open slot uses an equal share of allocPct.
 */
export function runBacktest(candles, desired, settings = {}) {
  const s = { ...DEFAULT_SETTINGS, ...settings };
  const maxTrades = Math.min(100, Math.max(1, Math.floor(Number(settings.maxTrades) || 1)));
  const n = candles.length;
  const allocValue = Number(s.allocPct);
  const alloc = Math.max(0, Number.isFinite(allocValue) ? allocValue : DEFAULT_SETTINGS.allocPct) / 100;
  const unitAlloc = alloc / maxTrades;
  const sideCost = Math.max(0, (Number(s.spreadBps) / 2 + Number(s.commissionBps) + Number(s.slippageBps)) / 1e4);
  const useSl = (s.slType === "atr" || s.slType === "pct") && Number(s.slValue) > 0;
  const useTp = (s.tpType === "atr" || s.tpType === "pct" || (s.tpType === "rr" && useSl)) && Number(s.tpValue) > 0;
  const needAtr = (useSl && s.slType === "atr") || (useTp && s.tpType === "atr");
  const atrArr = needAtr ? atr(candles.map((c) => c.high), candles.map((c) => c.low), candles.map((c) => c.close), 14) : null;
  const distance = (type, value, price, i) => {
    if (type === "pct") return (price * value) / 100;
    const a = atrArr && i > 0 ? atrArr[i - 1] : NaN;
    return Number.isFinite(a) ? a * value : NaN;
  };

  const times = new Array(n);
  const equity = new Array(n);
  const barReturns = new Array(n).fill(0);
  const position = new Array(n).fill(0);
  const trades = [];
  let eq = Number(s.capital);
  let lots = [];
  let blockSide = 0;

  const charge = (lot, price, rate = sideCost) => {
    if (!Number.isFinite(price) || !Number.isFinite(lot.quantity)) return;
    const amount = Math.abs(lot.quantity * price) * rate;
    eq -= amount;
    lot.pnl -= amount;
  };
  const moveLots = (activeLots, fromPrice, toPrice) => {
    if (!activeLots.length || !Number.isFinite(fromPrice) || !Number.isFinite(toPrice)) return;
    let totalPnl = 0;
    activeLots.forEach((lot) => {
      const pnl = lot.side * lot.quantity * (toPrice - fromPrice);
      lot.pnl += pnl;
      totalPnl += pnl;
    });
    eq += totalPnl;
  };
  const closeLot = (i, lot, price, reason) => {
    charge(lot, price);
    const notional = lot.quantity * lot.price;
    trades.push({
      side: lot.side,
      entryIdx: lot.idx,
      entryTime: candles[lot.idx].time,
      entryPrice: lot.price,
      exitIdx: i,
      exitTime: candles[i].time,
      exitPrice: price,
      ret: lot.eqBefore > 0 ? lot.pnl / lot.eqBefore : 0,
      positionRet: notional > 0 ? lot.pnl / notional : 0,
      pnl: lot.pnl,
      quantity: lot.quantity,
      notional,
      bars: i - lot.idx,
      reason,
      risk: lot.risk,
      rMultiple: Number.isFinite(lot.risk) && lot.risk > 0 ? (lot.side * (price - lot.price)) / lot.risk : NaN,
    });
  };
  const addLot = (i, side) => {
    if (!(eq > 0) || !(unitAlloc > 0)) return false;
    const price = entryFillPrice(side, candles[i - 1]?.close, candles[i], s);
    if (!Number.isFinite(price) || price <= 0) return false;
    const eqBefore = eq;
    const quantity = (eqBefore * unitAlloc) / price;
    const risk = useSl ? distance(s.slType, s.slValue, price, i) : NaN;
    const targetDistance = !useTp
      ? NaN
      : s.tpType === "rr"
        ? (Number.isFinite(risk) ? risk * s.tpValue : NaN)
        : distance(s.tpType, s.tpValue, price, i);
    const lot = {
      side,
      idx: i,
      price,
      eqBefore,
      quantity,
      pnl: 0,
      risk,
      best: price,
      enteredThisBar: true,
      stopPrice: Number.isFinite(risk) ? price - side * risk : NaN,
      targetPrice: Number.isFinite(targetDistance) ? price + side * targetDistance : NaN,
    };
    lots.push(lot);
    charge(lot, price);
    return true;
  };

  for (let i = 0; i < n; i += 1) {
    const candle = candles[i];
    times[i] = candle.time;
    const prevEq = i === 0 ? Number(s.capital) : equity[i - 1];

    if (lots.length && i > 0) {
      const elapsedDays = Math.max(0, (candle.time - candles[i - 1].time) / 86400);
      lots.forEach((lot) => {
        const dailyBps = lot.side === 1 ? Number(s.longFinancingBpsPerDay) : Number(s.shortFinancingBpsPerDay);
        const rate = Number.isFinite(dailyBps) ? (dailyBps / 1e4) * elapsedDays : 0;
        if (rate) charge(lot, candles[i - 1].close, rate);
      });
      moveLots(lots, candles[i - 1].close, candle.open);
    }

    const survivorsAfterGap = [];
    for (const lot of lots) {
      const gapStop = Number.isFinite(lot.stopPrice) && ((lot.side === 1 && candle.open <= lot.stopPrice) || (lot.side === -1 && candle.open >= lot.stopPrice));
      const gapTarget = Number.isFinite(lot.targetPrice) && ((lot.side === 1 && candle.open >= lot.targetPrice) || (lot.side === -1 && candle.open <= lot.targetPrice));
      if (gapStop || gapTarget) {
        closeLot(i, lot, candle.open, gapStop ? "stop" : "target");
        blockSide = lot.side;
      } else survivorsAfterGap.push(lot);
    }
    lots = survivorsAfterGap;

    let target = i > 0 ? Math.round(Number(desired[i - 1]) || 0) : 0;
    target = Math.min(maxTrades, Math.max(-maxTrades, target));
    if (!s.allowShort && target < 0) target = 0;
    if (blockSide !== 0) {
      if (Math.sign(target) === blockSide) target = 0;
      else blockSide = 0;
    }

    const targetSide = Math.sign(target);
    const openSide = lots.length ? lots[0].side : 0;
    if (openSide !== targetSide) {
      lots.slice().forEach((lot) => closeLot(i, lot, candle.open, "signal"));
      lots = [];
    }
    const desiredCount = targetSide === 0 ? 0 : Math.abs(target);
    while (lots.length > desiredCount) closeLot(i, lots.pop(), candle.open, "signal");
    while (lots.length < desiredCount) {
      if (!addLot(i, targetSide)) break;
    }

    if (lots.length) {
      const events = lots.map((lot) => {
        const hitStop = Number.isFinite(lot.stopPrice) && ((lot.side === 1 && candle.low <= lot.stopPrice) || (lot.side === -1 && candle.high >= lot.stopPrice));
        const hitTarget = Number.isFinite(lot.targetPrice) && ((lot.side === 1 && candle.high >= lot.targetPrice) || (lot.side === -1 && candle.low <= lot.targetPrice));
        const reason = hitStop ? "stop" : hitTarget ? "target" : "";
        return {
          lot,
          fromPrice: lot.enteredThisBar ? lot.price : candle.open,
          price: hitStop ? lot.stopPrice : hitTarget ? lot.targetPrice : candle.close,
          reason,
        };
      });
      events.forEach(({ lot, fromPrice, price }) => moveLots([lot], fromPrice, price));

      const survivorsAfterBar = [];
      for (const { lot, price, reason } of events) {
        if (reason) {
          closeLot(i, lot, price, reason);
          blockSide = lot.side;
        } else {
          if (s.trailing && Number.isFinite(lot.risk) && !lot.enteredThisBar) {
            lot.best = lot.side === 1 ? Math.max(lot.best, candle.high) : Math.min(lot.best, candle.low);
            const trail = lot.best - lot.side * lot.risk;
            lot.stopPrice = lot.side === 1 ? Math.max(lot.stopPrice, trail) : Math.min(lot.stopPrice, trail);
          }
          lot.enteredThisBar = false;
          survivorsAfterBar.push(lot);
        }
      }
      lots = survivorsAfterBar;
    }

    position[i] = lots.length ? lots[0].side : 0;
    equity[i] = eq;
    barReturns[i] = prevEq > 0 ? eq / prevEq - 1 : 0;
  }

  if (lots.length && n > 0) {
    lots.slice().forEach((lot) => closeLot(n - 1, lot, candles[n - 1].close, "end"));
    lots = [];
    equity[n - 1] = eq;
    barReturns[n - 1] = (n > 1 ? eq / equity[n - 2] : eq / Number(s.capital)) - 1;
    position[n - 1] = 0;
  }

  return { times, equity, barReturns, position, trades, settings: s };
}

/** Equal-weight portfolio of several runs on the same candles, rebalanced every bar. */
export function combinePortfolio(runs, capital) {
  if (!runs.length) return null;
  const n = runs[0].times.length;
  const equity = new Array(n);
  const barReturns = new Array(n).fill(0);
  const position = new Array(n).fill(0);
  let eq = capital;
  for (let i = 0; i < n; i += 1) {
    let r = 0;
    let inMarket = 0;
    runs.forEach((run) => {
      r += run.barReturns[i] / runs.length;
      if (run.position[i] !== 0) inMarket += 1;
    });
    eq *= 1 + r;
    equity[i] = eq;
    barReturns[i] = r;
    position[i] = inMarket / runs.length; // fraction of the portfolio invested
  }
  const trades = runs.flatMap((run) => run.trades.map((t) => ({ ...t, strategy: run.name })));
  trades.sort((a, b) => a.exitTime - b.exitTime);
  return { times: runs[0].times, equity, barReturns, position, trades };
}
